import useAuthStore from '@/stores/auth'
import type { User } from '@/data/schema/user'
import { ApiService } from './api/api'
import useToasterStore from '@/stores/toaster'

class AuthService {
	private authStore = useAuthStore()
	private toasterStore = useToasterStore()
	private api

	constructor() {
		this.api = new ApiService()
	}

	// *LOG IN & OUT*

	async isLoggedIn(): Promise<boolean> {
		if (
			!this.authStore.authenticationInProgress &&
			(!this.authStore.isLoggedIn || this.authStore.tokenHasExpired)
		) {
			this.authStore.isLoggedIn = false
			this.authStore.authenticationInProgress = true
			return false
		}

		return true
	}

	async authenticate(referer: string): Promise<boolean> {
		return new Promise((resolve, reject) => {
			// If local, bypass Azure
			if (import.meta.env.VITE_ENVIRONMENT === 'local') {
				;(async () => {
					try {
						const login = await this.login()
						const localUser: User = login.data as User
						this.loginUser(localUser)
						resolve(true)
					} catch (error) {
						reject(error)
					}
				})()
			} else {
				// Get tokens from Azure and attempt login
				this.getMe()
					.then(async (me) => {
						const accessToken: string = me[0].access_token
						const idToken: string = me[0].id_token
						this.authStore.setTokens(accessToken, idToken)
						return await this.login()
					})
					// Set auth store values
					.then((response) => {
						const user = response.data as User
						this.loginUser(user)
						resolve(true)
					})
					// Catch expired session, alert and redirect to Azure login
					.catch((error) => {
						if (error.response && error.response.status == 500) {
							this.toasterStore.error({
								text: 'An error occurred attempting signin. Please try again later',
								timeout: 0
							})
						} else if (error.response && error.response.status == 403) {
							this.toasterStore.error({
								text: 'Unauthorized Access. Please contact an admin.',
								timeout: 0
							})
						} else {
							this.toasterStore.error({
								text: 'Session expired. Redirecting...',
								timeout: 0
							})

							setTimeout(() => {
								this.redirectToLogin(referer)
							}, 2000)
						}

						reject(error)
					})
			}
		})
	}

	async login() {
		return this.api.get('/auth/user')
	}

	loginUser(userData: User) {
		this.setUser(userData)
		this.setLoggedIn(true)
	}

	setLoggedIn(value: boolean) {
		this.authStore.setLoggedIn(value)
	}

	// *USER DETAILS*

	setUser(user: User | null) {
		this.authStore.setUser(user)
	}

	getMe() {
		return fetch('/.auth/me', {
			method: 'GET'
		})
			.then((response) => {
				if (!response.ok) {
					return Promise.reject(response)
				}
				return response.json()
			})
			.then((data) => {
				return data
			})
			.catch((error) => {
				throw error
			})
	}

	// * REDIRECT TO LOGIN *

	redirectToLogin(currentUrl: string) {
		window.location.replace(
			'/.auth/login/aad?post_login_redirect_url=' + encodeURIComponent(currentUrl)
		)
	}

	// * PERMISSIONS *

	authorize(permissions: string[]): boolean {
		return this.authStore.isAbleTo(permissions)
	}

	// * USER IMPERSONATION *

	// getImpersonateUserId() {
	//   return this.authStore.impersonateUserId
	// }

	// isImpersonatingUser() {
	//   return this.authStore.impersonateUserId !== null
	// }

	// canImpersonateUser() {
	//   const user = this.authStore.user
	//   const masterUser = this.authStore.masterUser
	//   if (masterUser) {
	//     return masterUser.isAbleTo('impersonate-user')
	//   } else if (user) {
	//     return user.isAbleTo('impersonate-user')
	//   }
	//   return false
	// }

	// async impersonateUser(userId) {
	//   this.authStore.setMasterUser(this.authStore.user)
	//   this.authStore.setImpersonateUserId(userId)
	//   try {
	//     const response = await this.api.get('/auth/impersonate/' + userId) // Adjust URL as needed
	//     this.authStore.setUser(response.data)
	//   } catch (error) {
	//     console.error('Error during user impersonation:', error)
	//     // Handle error
	//   }
	// }

	// stopImpersonatingUser() {
	//   this.authStore.setImpersonateUserId(null)
	//   this.authStore.setUser(this.authStore.masterUser)
	//   // Replace Toastr with your notification system in Vue
	//   // e.g., this.$toast.info('Stopped Impersonating User');
	// }
}

export default AuthService
